import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import Decimal from 'decimal.js';
import { cloneDeep } from 'lodash-es';
import { Typography, Popover, CloseButton, TextField, Select, Grid, GridItem, MenuItem, Button, useStateInRef } from 'seagull';
import { useLocaleKeys } from '~/i18n';
import { DiscountModel, DiscountType } from '~/models';
import { utils } from '~/utils';
import styles from './ServicePriceEditor.module.scss';
function resetForm(cost, setPrice, setDiscount, setDiscountType) {
    var _ref;
    var _cost_discount;
    setDiscount(cost.discount ? cost.discount.amount().toNumber() : '');
    setDiscountType((_ref = (_cost_discount = cost.discount) === null || _cost_discount === void 0 ? void 0 : _cost_discount.type()) !== null && _ref !== void 0 ? _ref : DiscountType.Percent);
    switch(cost.pricingType){
        case 'fixed':
        case 'variable':
            setPrice(cost.price.toNumber());
            break;
        case 'range':
            setPrice({
                minPrice: cost.minPrice ? cost.minPrice.toNumber() : '',
                maxPrice: cost.maxPrice.toNumber()
            });
            break;
    }
}
export function ServicePriceEditor(props) {
    var _price_minPrice, _price_minPrice1;
    const { t: texts } = useLocaleKeys('components.servicePriceEditor');
    const getProps = useStateInRef(props);
    const { cost, editorAnchor, isOpen, onInputFocus, onInputBlur, isBillInStages } = props;
    const [price, setPrice] = useState('');
    const [discount, setDiscount] = useState('');
    const [discountType, setDiscountType] = useState(DiscountType.Percent);
    const unitName = cost.pricingType === 'variable' && (cost.unitName || 'hr');
    useEffect(()=>resetForm(cost, setPrice, setDiscount, setDiscountType), [
        cost
    ]);
    const discountedPrice = useMemo(()=>{
        const calcPrice = (price)=>utils.applyDiscount(price, discount ? DiscountModel.make(discountType, new Decimal(discount)) : null).toNumber();
        if (typeof price === 'number') {
            return calcPrice(price);
        } else if (typeof price === 'object' && price.maxPrice) {
            return {
                minPrice: typeof price.minPrice !== 'number' ? undefined : calcPrice(price.minPrice),
                maxPrice: calcPrice(price.maxPrice)
            };
        }
        return '';
    }, [
        discount,
        discountType,
        price
    ]);
    const priceError = useMemo(()=>{
        if (!price && price !== 0) {
            return 'Provide a price of 0 or more';
        } else if (typeof price !== 'number' && !price.maxPrice) {
            return 'Insert price';
        }
        return '';
    }, [
        price
    ]);
    const minPriceError = useMemo(()=>{
        if (price && typeof price !== 'number' && price.minPrice && price.maxPrice && price.minPrice >= price.maxPrice) {
            return "Min can't exceed or equal max";
        }
        return '';
    }, [
        price
    ]);
    const discountError = useMemo(()=>{
        if (discount !== '' && price !== '') {
            if (discountType === 'value') {
                if (typeof price === 'number' && price < discount) {
                    return "Discount value can't exceed the price";
                } else if (typeof price === 'object') {
                    if (price.minPrice && price.minPrice < discount) {
                        return "Discount value can't exceed min price";
                    } else if (typeof price.maxPrice === 'number' && price.maxPrice < discount) {
                        return "Discount value can't exceed max price";
                    }
                } else if (isBillInStages) {
                    return 'Must be a percentage when billing in stages';
                }
            } else if (discountType === 'percent' && discount > 100) {
                return 'Provide a value up to 100%';
            }
        }
        return '';
    }, [
        discount,
        discountType,
        price
    ]);
    const hasError = useMemo(()=>!!priceError || !!minPriceError || !!discountError, [
        discountError,
        minPriceError,
        priceError
    ]);
    function onPriceChange(e) {
        const newValue = e.currentTarget.value;
        const asNumber = +newValue;
        if (!newValue) {
            setPrice('');
        } else if (asNumber >= 0) {
            setPrice(asNumber);
        }
    }
    function onMinPriceChange(e) {
        const newValue = e.currentTarget.value;
        const asNumber = +newValue;
        if (asNumber < 0) return;
        setPrice({
            minPrice: !newValue ? '' : asNumber,
            maxPrice: price && typeof price === 'object' && 'maxPrice' in price ? price.maxPrice : ''
        });
    }
    function onMaxPriceChange(e) {
        const newValue = e.currentTarget.value;
        const asNumber = +newValue;
        if (asNumber < 0) return;
        setPrice({
            minPrice: price && typeof price === 'object' && 'maxPrice' in price ? price.minPrice : '',
            maxPrice: !newValue ? '' : asNumber
        });
    }
    function onDiscountChange(e) {
        const newValue = e.currentTarget.value;
        const asNumber = +newValue;
        if (!newValue) {
            setDiscount('');
        } else if (asNumber >= 0) {
            setDiscount(asNumber);
        }
    }
    function onSave(e) {
        var _cost_discount;
        e.preventDefault();
        if (hasError) return;
        const { cost, onCostChange, setIsOpen } = getProps();
        const edit = cloneDeep(cost);
        edit.discount = discount ? DiscountModel.make(discountType, new Decimal(discount), (_cost_discount = cost.discount) === null || _cost_discount === void 0 ? void 0 : _cost_discount.expiration()) : null;
        switch(edit.pricingType){
            case 'fixed':
            case 'variable':
                edit.price = new Decimal(+price);
                break;
            case 'range':
                if (price && typeof price === 'object') {
                    edit.minPrice = price.minPrice || price.minPrice === 0 ? new Decimal(+price.minPrice) : null;
                    edit.maxPrice = new Decimal(+price.maxPrice);
                }
                break;
        }
        onCostChange(edit);
        setIsOpen(false);
    }
    const onDismiss = useCallback(()=>{
        const { cost, setIsOpen } = getProps();
        setIsOpen(false);
        setTimeout(()=>resetForm(cost, setPrice, setDiscount, setDiscountType), 100); // apr. time takes the popover to close
    }, []);
    return /*#__PURE__*/ _jsx(Popover, {
        classes: {
            paper: styles.popover
        },
        open: isOpen,
        anchorEl: editorAnchor,
        onClose: onDismiss,
        children: /*#__PURE__*/ _jsxs("form", {
            onSubmit: onSave,
            className: styles.popoverForm,
            children: [
                /*#__PURE__*/ _jsx(CloseButton, {
                    bi: {
                        name: 'close_service_price_editor'
                    },
                    className: styles.closeBtn,
                    testId: "price-editor-popover-close",
                    onClick: onDismiss
                }),
                /*#__PURE__*/ _jsxs(Grid, {
                    children: [
                        typeof price === 'number' || price === '' ? /*#__PURE__*/ _jsx(GridItem, {
                            span: 12,
                            children: /*#__PURE__*/ _jsx(TextField, {
                                bi: {
                                    name: 'service_price'
                                },
                                mobile: {
                                    renderAsCard: true
                                },
                                collapseHelperText: false,
                                isMoney: true,
                                autoFocus: true,
                                inputProps: {
                                    min: 0
                                },
                                testId: "price-editor-value",
                                label: texts.priceLabel(),
                                prefix: "$",
                                suffix: unitName && "/".concat(unitName),
                                value: price,
                                onChange: onPriceChange,
                                error: !!priceError,
                                helperText: priceError,
                                onFocus: onInputFocus,
                                onBlur: onInputBlur
                            })
                        }) : /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(GridItem, {
                                    span: 6,
                                    children: /*#__PURE__*/ _jsx(TextField, {
                                        bi: {
                                            name: 'service_price_min'
                                        },
                                        mobile: {
                                            renderAsCard: true
                                        },
                                        collapseHelperText: false,
                                        isMoney: true,
                                        autoFocus: true,
                                        inputProps: {
                                            min: 0
                                        },
                                        testId: "price-editor-min-value",
                                        label: texts.minLabel(),
                                        prefix: "$",
                                        value: (_price_minPrice = price.minPrice) !== null && _price_minPrice !== void 0 ? _price_minPrice : '',
                                        onChange: onMinPriceChange,
                                        error: !!minPriceError,
                                        helperText: minPriceError,
                                        onFocus: onInputFocus,
                                        onBlur: onInputBlur
                                    })
                                }),
                                /*#__PURE__*/ _jsx(GridItem, {
                                    span: 6,
                                    children: /*#__PURE__*/ _jsx(TextField, {
                                        bi: {
                                            name: 'service_price_max'
                                        },
                                        mobile: {
                                            renderAsCard: true
                                        },
                                        collapseHelperText: false,
                                        isMoney: true,
                                        autoFocus: true,
                                        inputProps: {
                                            min: (_price_minPrice1 = price.minPrice) !== null && _price_minPrice1 !== void 0 ? _price_minPrice1 : 0
                                        },
                                        testId: "price-editor-max-value",
                                        label: texts.maxLabel(),
                                        prefix: "$",
                                        value: price.maxPrice,
                                        onChange: onMaxPriceChange,
                                        error: !!priceError,
                                        helperText: priceError,
                                        onFocus: onInputFocus,
                                        onBlur: onInputBlur
                                    })
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(GridItem, {
                            span: 12,
                            children: /*#__PURE__*/ _jsx(TextField, {
                                bi: {
                                    name: 'service_price_discount'
                                },
                                mobile: {
                                    renderAsCard: true
                                },
                                collapseHelperText: false,
                                isPercentage: discountType === 'percent',
                                isMoney: discountType === 'value',
                                inputProps: {
                                    min: 0
                                },
                                testId: "price-editor-discount",
                                label: discountType === 'percent' ? 'Discount' : 'Unit discount',
                                value: discount,
                                onChange: onDiscountChange,
                                error: !!discountError,
                                helperText: discountError,
                                suffix: /*#__PURE__*/ _jsx("div", {
                                    children: /*#__PURE__*/ _jsxs(Select, {
                                        bi: {
                                            name: 'service_price_discount_type'
                                        },
                                        mobile: {
                                            renderAsCard: true
                                        },
                                        collapseHelperText: false,
                                        value: discountType,
                                        onChange: (e)=>setDiscountType(e.target.value),
                                        preventOutline: true,
                                        children: [
                                            /*#__PURE__*/ _jsx(MenuItem, {
                                                disabled: isBillInStages,
                                                tooltipProps: {
                                                    title: isBillInStages ? 'Must be a percentage when billing in stages' : ''
                                                },
                                                value: "value",
                                                children: "$"
                                            }),
                                            /*#__PURE__*/ _jsx(MenuItem, {
                                                value: "percent",
                                                children: "%"
                                            })
                                        ]
                                    })
                                }),
                                onFocus: onInputFocus,
                                onBlur: onInputBlur
                            })
                        })
                    ]
                }),
                !!discount && /*#__PURE__*/ _jsxs("div", {
                    "data-testid": "price-editor-final-price",
                    children: [
                        /*#__PURE__*/ _jsxs(Typography, {
                            as: "span",
                            weight: "normal",
                            children: [
                                "Price after discount:",
                                ' '
                            ]
                        }),
                        !hasError && /*#__PURE__*/ _jsx(Typography, {
                            as: "span",
                            children: price === '' || typeof price === 'number' || discountedPrice === '' || typeof discountedPrice === 'number' ? /*#__PURE__*/ _jsxs(Typography, {
                                as: "span",
                                children: [
                                    "$",
                                    typeof discountedPrice === 'number' && utils.formatNumberWithCommas(discountedPrice),
                                    unitName && "/".concat(unitName)
                                ]
                            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    !!discountedPrice.minPrice && !!price.minPrice && /*#__PURE__*/ _jsxs(Typography, {
                                        as: "span",
                                        children: [
                                            utils.formatDollarAmount(discountedPrice.minPrice),
                                            ' - '
                                        ]
                                    }),
                                    !discountedPrice.minPrice && /*#__PURE__*/ _jsx(Typography, {
                                        as: "span",
                                        children: 'Up to '
                                    }),
                                    /*#__PURE__*/ _jsxs(Typography, {
                                        as: "span",
                                        children: [
                                            "$",
                                            utils.formatNumberWithCommas(discountedPrice.maxPrice)
                                        ]
                                    })
                                ]
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: styles.popoverFooter,
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'cancel_service_price_edit'
                            },
                            type: "button",
                            skin: "primaryOutline",
                            onClick: onDismiss,
                            testId: "price-editor-cancel",
                            children: "Cancel"
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'save_service_price'
                            },
                            type: "submit",
                            disabled: hasError,
                            testId: "price-editor-save",
                            children: "Save"
                        })
                    ]
                })
            ]
        })
    });
}
